import useTheme from "../../../../common/hooks/useTheme";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../store/platform-slice";
import React, { useState, useMemo } from "react";
import ServiceContainer from "./ServiceContainer";
import { Theme } from "../../../../providers/ThemeProvider";
import SchedulingContent from "./SchedulingContent";
import {
  SchedulingPage,
  ServicesPage,
} from "../../../new-reports/types/newReportTypes";
import FilterSelect from "../prescriptionTab/FilterSelect";
import { enReportServiceType } from "../seviceBuilder/helper";

const BioOptimizationPlanTab = () => {
  const theme: Theme = useTheme();
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>(
    []
  );

  const reportData = useSelector(
    (state: RootStatePlatform) =>
      state.platform.dataPlatformServer.prescriptionPlan
  );

  const scheduleData = reportData.find(
    (p) => p.id === "scheduling-1" && p.pageType === "scheduling"
  ) as SchedulingPage | undefined;

  const servicesData = reportData.find(
    (p) => p.id === "services-page-1" && p.pageType === "services"
  ) as ServicesPage | undefined;

  // Service type filter options - dynamically generated from services data
  const serviceTypeOptions = useMemo(() => {
    if (!servicesData?.services) return [];

    const serviceTypesSet = new Set<number>();
    servicesData.services.forEach((service) => {
      if (service.serviceTypeCode) {
        serviceTypesSet.add(service.serviceTypeCode);
      }
    });

    return Array.from(serviceTypesSet)
      .sort((a, b) => a - b)
      .map((serviceTypeCode) => ({
        key: String(serviceTypeCode),
        label: enReportServiceType[serviceTypeCode] || `Type ${serviceTypeCode}`,
        origin: enReportServiceType[serviceTypeCode] || `Type ${serviceTypeCode}`,
      }));
  }, [servicesData]);

  const handleServiceTypeSelectionChange = (selected: string[]) => {
    setSelectedServiceTypes(selected);
  };

  // Filtered services data
  const filteredServicesData = useMemo(() => {
    if (!servicesData) return undefined;

    // If no filter is selected, return original data
    if (selectedServiceTypes.length === 0) {
      return servicesData;
    }

    // Filter services based on selected service types
    const filteredServices = servicesData.services.filter((service) =>
      selectedServiceTypes.includes(String(service.serviceTypeCode))
    );

    return {
      ...servicesData,
      services: filteredServices,
    };
  }, [servicesData, selectedServiceTypes]);

  // Check if there are no services
  const hasNoServices = !servicesData?.services || servicesData.services.length === 0;
  const hasNoFilteredServices = filteredServicesData?.services?.length === 0;
  const isFiltered = selectedServiceTypes.length > 0;

  return (
    <div style={styles.innerContainer(theme)}>
      {/* Title and Filter Row */}
      <div style={styles.titleRow}>
        <div style={styles.title(theme)}>Bio Optimization Plan</div>
        {serviceTypeOptions.length > 0 && (
          <div
            style={{
              width: "250px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <FilterSelect
              title="Service Type"
              options={serviceTypeOptions}
              selectedKeys={selectedServiceTypes}
              onSelectionChange={handleServiceTypeSelectionChange}
            />
          </div>
        )}
      </div>

      {/* Services Section */}
      {hasNoServices ? (
        <div style={styles.emptyStateContainer(theme)}>
          <div style={styles.emptyStateTitle(theme)}>No Services Available</div>
          <div style={styles.emptyStateText(theme)}>
            There are no services configured for this bio optimization plan.
          </div>
        </div>
      ) : hasNoFilteredServices ? (
        <div style={styles.emptyStateContainer(theme)}>
          <div style={styles.emptyStateTitle(theme)}>No Matching Services</div>
          <div style={styles.emptyStateText(theme)}>
            No services match the selected service type filter. Try adjusting your filter selection.
          </div>
        </div>
      ) : (
        <ServiceContainer
          data={filteredServicesData}
          title="Available services"
        />
      )}

      {/* Scheduling Section */}
      {scheduleData && scheduleData.schedule.length > 0 && (
        <>
          <div style={styles.sectionTitle(theme)}>Scheduling treatment</div>
          <SchedulingContent
            schedule={scheduleData.schedule}
            labels={scheduleData.labels}
          />
        </>
      )}
    </div>
  );
};

export default BioOptimizationPlanTab;

const styles = {
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    gap: "16px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    lineHeight: "28px",
  }),
  sectionTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    marginBottom: "16px",
  }),
  innerContainer: (theme: any) => ({
    width: "100%",
  }),
  cardContainer: (theme: any) => ({
    display: "flex",
    flexDirection: "row" as "row",
    gap: "16px",
    marginBottom: "32px",
  }),
  emptyStateContainer: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "48px 32px",
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "24px",
    textAlign: "center" as "center",
  }),
  emptyStateTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "18px",
    marginBottom: "8px",
  }),
  emptyStateText: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    maxWidth: "400px",
  }),
};
